import React, { useState, useContext, useEffect, useCallback } from 'react';
import CharacterPanel from './Components/CharacterPanel';
import './main.css';
import './Login.css'; // Import Login.css for theme variables
import UserList from './UserList';
import MapGrid from './MapGrid';
import RoomInfo from './RoomInfo';
import Trainer from './Components/trainer/Trainer';
import ChatInput from './Components/ChatInput';
import ChatConsole from './Components/ChatConsole';
import DualConsoleContainer from './Components/DualConsoleContainer';
import StaminaBar from './Components/StaminaBar';
import HealthBar from './Components/HealthBar';
import { AppState } from "./Home";
import TrainStat from './Components/modals/TrainStat';
import Shop from './Components/Shop/Shop';
import Hotkeys from './Components/ActionBar/Hotkeys';
import HotkeyEdit from './Components/ActionBar/HotkeyEdit';
import PlayerActions from './Components/ActionBar/PlayerActions';
import Inventory from './Components/Inventory/Inventory';
import Help from './Components/Help/Help';
import NotificationBar from './Components/Notification/NotificationBar';
import Cooldowns from './Components/Cooldowns';
import BuffPanel from './Components/buffs/BuffPanel';
import ManaBar from './Components/ManaBar';
import Craft from './Components/Shop/Craft';
import AbilityPanel from './Components/trainer/AbilityPanel';
import Trade from './Components/Trade/Trade';
import ThemeSwitcher from './Components/ThemeSwitcher';
import ConsoleContextMenu from './Components/ConsoleContextMenu';

import { useCraftStore, useAbilityStore, useLearnStore, useVaultStore, useTradeStore, usePlayerStore, useMessageStore } from './store/store';
import { getHotkeyStore, initializeHotkeys } from './store/hotkeyStore';
import Vault from './Components/Vault/Vault';

function Main({ sendMessage, roomInfo, mapInfo, position, shopList, shopInfo, buffs, cooldowns, removeCooldowns, notifications, updateNotification }) {
    const context = useContext(AppState);
    const { stamina, statPoints } = context;
    const [openModals, setOpenModals] = useState(new Set());
    
    // Console layout state - showSecondConsole from store for cross-component access
    const showSecondConsole = useMessageStore((state) => state.showSecondConsole);
    const setShowSecondConsole = useMessageStore((state) => state.setShowSecondConsole);
    
    const [consoleLayout, setConsoleLayout] = useState(() => {
        const saved = localStorage.getItem('console-layout');
        return saved === 'horizontal' || saved === 'vertical' ? saved : 'horizontal';
    });
    const [consoleSwapped, setConsoleSwapped] = useState(() => {
        return localStorage.getItem('console-swapped') === 'true';
    });
    
    // Save console state to localStorage (showSecondConsole persisted by Zustand store)
    useEffect(() => {
        localStorage.setItem('console-layout', consoleLayout);
    }, [consoleLayout]);
    
    useEffect(() => {
        localStorage.setItem('console-swapped', consoleSwapped.toString());
    }, [consoleSwapped]);
    
    // Context menu state
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    
    // Get timestamps state from store
    const showTimestamps = useMessageStore((state) => state.showTimestamps);
    
    // Get font settings from store
    const consoleFontFamily = useMessageStore((state) => state.consoleFontFamily);
    const consoleFontSize = useMessageStore((state) => state.consoleFontSize);
    
    // Context menu handlers
    const handleContextMenu = useCallback((x, y) => {
        setContextMenu({ visible: true, x, y });
    }, []);
    
    const closeContextMenu = useCallback(() => {
        setContextMenu({ visible: false, x: 0, y: 0 });
    }, []);
    
    const handleToggleTimestamps = useCallback(() => {
        useMessageStore.getState().setShowTimestamps(!showTimestamps);
        closeContextMenu();
    }, [showTimestamps, closeContextMenu]);
    
    const handleToggleSecondConsole = useCallback(() => {
        // If turning off, also reset swap
        if (showSecondConsole) {
            setConsoleSwapped(false);
        }
        setShowSecondConsole(!showSecondConsole);
        closeContextMenu();
    }, [showSecondConsole, setShowSecondConsole, closeContextMenu]);
    
    const handleLayoutChange = useCallback((layout) => {
        setConsoleLayout(layout);
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleToggleSwapped = useCallback(() => {
        setConsoleSwapped(prev => !prev);
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleFontFamilyChange = useCallback((font) => {
        useMessageStore.getState().setConsoleFontFamily(font);
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleFontSizeChange = useCallback((size) => {
        useMessageStore.getState().setConsoleFontSize(size);
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleClearConsole1 = useCallback(() => {
        useMessageStore.getState().clearMessages();
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleClearConsole2 = useCallback(() => {
        useMessageStore.getState().clearMessages2();
        closeContextMenu();
    }, [closeContextMenu]);
    
    const handleClearAllConsoles = useCallback(() => {
        useMessageStore.getState().clearAllMessages();
        closeContextMenu();
    }, [closeContextMenu]);
    
    // Get player info from the store
    const playerInfo = usePlayerStore((state) => state.pInfo);
    
    // Get character ID for hotkey persistence
    const characterId = playerInfo?.name;

    // Initialize hotkeys, mouse options, and ctrl+click options for this character when it changes
    useEffect(() => {
        if (characterId) {
            const store = initializeHotkeys(characterId);
            
            // Ensure we have the store and it has the expected structure
            if (store && store.getState) {
                const state = store.getState();
                
                // If mouseOptions doesn't exist in the store (for backwards compatibility)
                if (!state.mouseOptions) {
                    // Initialize with default mouse options
                    const defaultMouseOptions = [
                        { key: 'M1', name: "Attack", cmd: "/a {target}" },
                        { key: 'M2', name: "Examine", cmd: "/examine {target}" },
                        { key: 'M3', name: "Chat", cmd: "/t {target} hi" },
                        { key: 'M4', name: "", cmd: "" },
                        { key: 'M5', name: "", cmd: "" },
                    ];
                    state.setMouseOptions(defaultMouseOptions);
                }
                
                // If ctrlClickOptions doesn't exist in the store (for backwards compatibility)
                if (!state.ctrlClickOptions) {
                    // Initialize with default ctrl+click options
                    const defaultCtrlClickOptions = {
                        leftClick: { name: "Attack", cmd: "/a {target}" },
                        rightClick: { name: "Examine", cmd: "/examine {target}" }
                    };
                    state.setCtrlClickOptions(defaultCtrlClickOptions);
                }
            }
        }
    }, [characterId]);

    // Add theme handling
    const [currentTheme, setCurrentTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'default';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', currentTheme);
        localStorage.setItem('theme', currentTheme);
    }, [currentTheme]);

    const handleThemeChange = (theme) => {
        setCurrentTheme(theme);
    };

    const showCraft = useCraftStore((state)=> state.showCraft);
    const showAbilities = useAbilityStore((state) => state.show);
    const setShowAbilities = useAbilityStore((state) => state.setShow);
    const showLearn = useLearnStore((state) => state.show);
    const showVault = useVaultStore((state) => state.show);
    const showTrade = useTradeStore((state) => state.show);

    // Get the hotkey store for this character
    const hotkeyStore = characterId ? getHotkeyStore(characterId) : null;
    
    const displayModal = useCallback((type) => {
        setOpenModals(prev => {
            const newSet = new Set(prev);
            // Handle string type modals
            if (typeof type === 'string') {
                newSet.add(type);
            }
            // Handle object type modals (for additional parameters)
            else if (type && typeof type === 'object') {
                // Create a unique key for object modals
                const modalKey = `obj-${type.type}-${type.initialSet || 0}`;
                newSet.add(modalKey);
                // Store the full object in a separate map for retrieval
                if (!window.modalParams) window.modalParams = new Map();
                window.modalParams.set(modalKey, type);
            }
            return newSet;
        });
    }, []);

    function closeModal(modalId) {
        setOpenModals(prev => {
            const newSet = new Set(prev);
            newSet.delete(modalId);
            // Clean up stored params if it exists
            if (window.modalParams) {
                window.modalParams.delete(modalId);
            }
            return newSet;
        });
    }

    // Keyboard shortcuts for toggling modals and timestamps
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check if Alt key is pressed FIRST (before checking input fields)
            // This ensures Alt+R works even when typing
            if (e.altKey && !e.ctrlKey && !e.metaKey) {
                const key = e.key?.toLowerCase();
                const code = e.code;
                
                // Handle Alt+T for timestamps
                if (key === 't' || code === 'KeyT') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    // Toggle timestamps
                    const currentState = useMessageStore.getState().showTimestamps;
                    const newState = !currentState;
                    useMessageStore.getState().setShowTimestamps(newState);
                    
                    // Add feedback message
                    const feedbackText = newState ? '>> Timestamps enabled' : '>> Timestamps disabled';
                    useMessageStore.getState().addMessage([{
                        text: feedbackText,
                        color: '#ffffff',
                        fontWeight: '500',
                        textDecoration: 'none',
                        isLink: false,
                        linkCommand: null,
                        key: 'feedback',
                        className: 'system-message'
                    }], null, null);
                    return;
                }
                
                // Handle Alt+R for second console toggle
                if (code === 'KeyR' || key === 'r') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    setShowSecondConsole(prev => {
                        const newState = !prev;
                        // Add feedback message with the new state
                        const feedbackText = newState ? '>> Second console enabled' : '>> Second console disabled';
                        useMessageStore.getState().addMessage([{
                            text: feedbackText,
                            color: '#ffffff',
                            fontWeight: '500',
                            textDecoration: 'none',
                            isLink: false,
                            linkCommand: null,
                            key: 'feedback-console',
                            className: 'system-message'
                        }], null, null);
                        return newState;
                    });
                    return;
                }
            }
            
            // Don't trigger shortcuts if user is typing in an input field (for other shortcuts)
            const target = e.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                // return;
            }
            
            // Check if Ctrl key is pressed (or Cmd on Mac)
            if (e.ctrlKey || e.metaKey) {
                const key = e.key.toLowerCase();
                const code = e.code;
                
                // Handle Ctrl+Shift shortcuts (alternative to Alt shortcuts for Chrome compatibility)
                if (e.shiftKey) {
                    // Ctrl+Shift+T for timestamps
                    if (key === 't' || code === 'KeyT') {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        
                        // Toggle timestamps (same logic as Alt+T)
                        const currentState = useMessageStore.getState().showTimestamps;
                        const newState = !currentState;
                        useMessageStore.getState().setShowTimestamps(newState);
                        
                        const feedbackText = newState ? '>> Timestamps enabled' : '>> Timestamps disabled';
                        useMessageStore.getState().addMessage([{
                            text: feedbackText,
                            color: '#ffffff',
                            fontWeight: '500',
                            textDecoration: 'none',
                            isLink: false,
                            linkCommand: null,
                            key: 'feedback',
                            className: 'system-message'
                        }], null, null);
                        return;
                    }
                    
                    // Ctrl+Shift+R for second console
                    if (key === 'r' || code === 'KeyR') {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        
                        setShowSecondConsole(prev => {
                            const newState = !prev;
                            const feedbackText = newState ? '>> Second console enabled' : '>> Second console disabled';
                            useMessageStore.getState().addMessage([{
                                text: feedbackText,
                                color: '#ffffff',
                                fontWeight: '500',
                                textDecoration: 'none',
                                isLink: false,
                                linkCommand: null,
                                key: 'feedback-console',
                                className: 'system-message'
                            }], null, null);
                            return newState;
                        });
                        return;
                    }
                }
                
                // Check if this is one of our shortcuts and prevent default immediately
                if (key === 'i' || key === 'a' || key === 's' || key === 'h') {
                    e.preventDefault();
                    e.stopPropagation();
                }
                
                switch (key) {
                    case 'i':
                        // Toggle inventory modal
                        setOpenModals(prev => {
                            if (prev.has('inventory')) {
                                const newSet = new Set(prev);
                                newSet.delete('inventory');
                                return newSet;
                            } else {
                                const newSet = new Set(prev);
                                newSet.add('inventory');
                                return newSet;
                            }
                        });
                        break;
                    case 'a':
                        // Toggle abilities modal
                        if (showAbilities) {
                            setShowAbilities(false);
                        } else {
                            sendMessage('600/alist');
                        }
                        break;
                    case 's':
                        // Toggle train stats modal
                        setOpenModals(prev => {
                            if (prev.has('trainstats')) {
                                const newSet = new Set(prev);
                                newSet.delete('trainstats');
                                return newSet;
                            } else {
                                const newSet = new Set(prev);
                                newSet.add('trainstats');
                                return newSet;
                            }
                        });
                        break;
                    case 'h':
                        // Toggle hotkey modal
                        setOpenModals(prev => {
                            // Check for both string and object type hotkey modals
                            const hasHotkeyModal = prev.has('hotkey') || 
                                Array.from(prev).some(key => key.startsWith('obj-hotkey-'));
                            if (hasHotkeyModal) {
                                // Close all hotkey modals
                                const newSet = new Set(prev);
                                newSet.delete('hotkey');
                                Array.from(prev).forEach(key => {
                                    if (key.startsWith('obj-hotkey-')) {
                                        newSet.delete(key);
                                        if (window.modalParams) {
                                            window.modalParams.delete(key);
                                        }
                                    }
                                });
                                return newSet;
                            } else {
                                const newSet = new Set(prev);
                                newSet.add('hotkey');
                                return newSet;
                            }
                        });
                        break;
                }
            }
        };

        // Use capture phase to catch the event before browser handles it
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showAbilities, sendMessage, setShowAbilities, setShowSecondConsole]);
    
    // Get actions and setActions from the hotkey store or use default if store not available
    const actions = hotkeyStore ? hotkeyStore((state) => state.actions) : [
        [
            { key: 'F1', name: "Attack", cmd: "/a" },
            { key: 'F2', name: "Stop Attack", cmd: "/stop" },
            { key: 'F3', name: "Edit Hotkeys", cmd: "/hotkey" },
            { key: 'F4', name: "Help", cmd: "/help" },
            { key: 'F5', name: "Quests", cmd: "/quests" },
            { key: 'F6', name: "", cmd: "" },
            { key: 'F7', name: "", cmd: "" },
            { key: 'F8', name: "", cmd: "" },
            { key: 'F9', name: "", cmd: "" },
        ],
        [
            { key: 'F1', name: "", cmd: "" },
            { key: 'F2', name: "", cmd: "" },
            { key: 'F3', name: "", cmd: "" },
            { key: 'F4', name: "", cmd: "" },
            { key: 'F5', name: "", cmd: "" },
            { key: 'F6', name: "", cmd: "" },
            { key: 'F7', name: "", cmd: "" },
            { key: 'F8', name: "", cmd: "" },
            { key: 'F9', name: "", cmd: "" },
        ],
        [
            { key: 'F1', name: "", cmd: "" },
            { key: 'F2', name: "", cmd: "" },
            { key: 'F3', name: "", cmd: "" },
            { key: 'F4', name: "", cmd: "" },
            { key: 'F5', name: "", cmd: "" },
            { key: 'F6', name: "", cmd: "" },
            { key: 'F7', name: "", cmd: "" },
            { key: 'F8', name: "", cmd: "" },
            { key: 'F9', name: "", cmd: "" },
        ],
        [
            { key: 'F1', name: "", cmd: "" },
            { key: 'F2', name: "", cmd: "" },
            { key: 'F3', name: "", cmd: "" },
            { key: 'F4', name: "", cmd: "" },
            { key: 'F5', name: "", cmd: "" },
            { key: 'F6', name: "", cmd: "" },
            { key: 'F7', name: "", cmd: "" },
            { key: 'F8', name: "", cmd: "" },
            { key: 'F9', name: "", cmd: "" },
        ],
    ];
    
    const setActions = hotkeyStore ? hotkeyStore((state) => state.setActions) : () => {};

    // Determine which modals to show
    const renderModals = () => {
        const modals = [];
        
        openModals.forEach(modalKey => {
            // Handle string type modals
            if (!modalKey.startsWith('obj-')) {
                switch (modalKey) {
                case 'trainstats':
                        modals.push(
                            <TrainStat 
                                key="trainstats"
                                displayModal={() => closeModal('trainstats')} 
                                statPoints={statPoints} 
                                sendMessage={sendMessage} 
                            />
                        );
                        break;
                case 'shop':
                        modals.push(
                            <Shop 
                                key="shop"
                                displayModal={() => closeModal('shop')} 
                                shopList={shopList} 
                                shopInfo={shopInfo} 
                                sendMessage={sendMessage} 
                            />
                        );
                        break;
                case 'hotkey':
                        modals.push(
                            <HotkeyEdit 
                                key="hotkey"
                                displayModal={() => closeModal('hotkey')} 
                                actions={actions} 
                                setActions={setActions} 
                                characterId={characterId} 
                            />
                        );
                        break;
                case 'inventory':
                        modals.push(
                            <Inventory 
                                key="inventory"
                                displayModal={() => closeModal('inventory')} 
                                sendMessage={sendMessage} 
                            />
                        );
                        break;
                case 'help':
                        modals.push(
                            <Help 
                                key="help"
                                displayModal={() => closeModal('help')}
                                consoleLayout={consoleLayout}
                                onConsoleLayoutChange={setConsoleLayout}
                            />
                        );
                        break;
            }
        }
        // Handle object type modals (for additional parameters)
            else if (modalKey.startsWith('obj-')) {
                const params = window.modalParams?.get(modalKey);
                if (params && params.type === 'hotkey') {
                    modals.push(
                        <HotkeyEdit 
                            key={modalKey}
                            displayModal={() => closeModal(modalKey)} 
                        actions={actions} 
                        setActions={setActions} 
                        characterId={characterId}
                            initialSet={params.initialSet || 0} 
                        />
                    );
            }
        }
        });
        
        return modals;
    };

    return (
        <>
            {showCraft && (<Craft sendMessage={sendMessage} />)}
            {showAbilities && (<AbilityPanel sendMessage={sendMessage}/>)}
            {showLearn  && (<Trainer sendMessage={sendMessage}/>)}
            {showVault && (<Vault sendMessage={sendMessage}/>)}
            {/* {showTrade && (<Trade />)} */}
            
            {renderModals()}

            <div className='container' >
                <div className='left'>
                    <CharacterPanel sendMessage={sendMessage} />
                    <HealthBar />
                    <ManaBar />
                    <UserList displayModal={displayModal} sendMessage={sendMessage} characterId={characterId} />
                    {cooldowns.length > 0 && <Cooldowns cooldowns={cooldowns} removeCooldowns={removeCooldowns} />}
                    <StaminaBar stamina={stamina} />
                    <PlayerActions displayModal={displayModal} sendMessage={sendMessage} />
                    <Hotkeys displayModal={displayModal} actions={actions} setActions={setActions} sendMessage={sendMessage} characterId={characterId} />
                </div>
                <div className='room-grid'>
                    <div className='room-info-area main-border'>
                        <MapGrid mapInfo={mapInfo} position={position} area={roomInfo.area} />
                        <RoomInfo sendMessage={sendMessage} roomInfo={roomInfo} />
                    </div>
                    {buffs.length > 0 && <BuffPanel buffs={buffs} />}
                    <div className='layer-test'>
                        <NotificationBar sendMessage={sendMessage} notifications={notifications} updateNotification={updateNotification} />
                        <DualConsoleContainer 
                            sendMessage={sendMessage} 
                            showSecondConsole={showSecondConsole}
                            layout={consoleLayout}
                            consoleSwapped={consoleSwapped}
                            onContextMenu={handleContextMenu}
                            consoleFontFamily={consoleFontFamily}
                            consoleFontSize={consoleFontSize}
                        />
                    </div>
                    <ChatInput sendMessage={sendMessage} currentTheme={currentTheme} onThemeChange={handleThemeChange} />
                </div>
            </div>
            
            {/* Console Context Menu */}
            {contextMenu.visible && (
                <ConsoleContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={closeContextMenu}
                    showSecondConsole={showSecondConsole}
                    onToggleSecondConsole={handleToggleSecondConsole}
                    consoleLayout={consoleLayout}
                    onLayoutChange={handleLayoutChange}
                    consoleSwapped={consoleSwapped}
                    onToggleSwapped={handleToggleSwapped}
                    showTimestamps={showTimestamps}
                    onToggleTimestamps={handleToggleTimestamps}
                    consoleFontFamily={consoleFontFamily}
                    onFontFamilyChange={handleFontFamilyChange}
                    consoleFontSize={consoleFontSize}
                    onFontSizeChange={handleFontSizeChange}
                    onClearConsole1={handleClearConsole1}
                    onClearConsole2={handleClearConsole2}
                    onClearAllConsoles={handleClearAllConsoles}
                />
            )}
        </>
    );
}

export default Main;